/**
 * Theme Generator
 *
 * Generates CSS variables from color preset and user preferences
 * Handles light/dark mode conversion automatically
 */

import { applyDensity, BASE_SPACING, type DensityLevel } from '../tokens/spacing';
import type { ColorPreset, ColorScale } from '../tokens/colors';

// ============================================================================
// TYPES
// ============================================================================

export interface ThemeConfig {
  /** Color preset */
  colorPreset: ColorPreset;
  /** Light or dark mode */
  mode: 'light' | 'dark';
  /** Font family ID */
  fontFamily: string;
  /** Density level */
  density: DensityLevel;
}

export interface ThemeVariables {
  /** Color CSS variables */
  colors: Record<string, string>;
  /** Spacing CSS variables */
  spacing: Record<string, string>;
  /** Typography CSS variables */
  typography: Record<string, string>;
}

// ============================================================================
// COLOR CONVERSION
// ============================================================================

/**
 * Convert hex to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Convert RGB to HSL
 */
const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Convert HSL to hex
 */
const hslToHex = (h: number, s: number, l: number): string => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Adjust color for dark mode
 * Inverts lightness while maintaining hue and saturation
 */
const adjustForDarkMode = (hex: string): string => {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Invert lightness (light becomes dark, dark becomes light)
  const invertedLightness = 100 - hsl.l;

  // Adjust saturation slightly for better visibility
  const adjustedSaturation = hsl.s * 0.9;

  return hslToHex(hsl.h, adjustedSaturation, invertedLightness);
};

// ============================================================================
// COLOR SCALE CONVERSION
// ============================================================================

/**
 * Convert color scale for dark mode
 */
const convertScaleForDarkMode = (scale: ColorScale): ColorScale => ({
  50: adjustForDarkMode(scale[900]), // Darkest becomes lightest
  100: adjustForDarkMode(scale[800]),
  200: adjustForDarkMode(scale[700]),
  300: adjustForDarkMode(scale[600]),
  400: adjustForDarkMode(scale[500]),
  500: adjustForDarkMode(scale[400]), // Middle stays middle-ish
  600: adjustForDarkMode(scale[300]),
  700: adjustForDarkMode(scale[200]),
  800: adjustForDarkMode(scale[100]),
  900: adjustForDarkMode(scale[50]), // Lightest becomes darkest
});

// ============================================================================
// CSS VARIABLE GENERATION
// ============================================================================

/**
 * Generate color CSS variables from color scale
 */
const generateColorVars = (
  prefix: string,
  scale: ColorScale,
  mode: 'light' | 'dark'
): Record<string, string> => {
  const vars: Record<string, string> = {};
  const finalScale = mode === 'dark' ? convertScaleForDarkMode(scale) : scale;

  Object.entries(finalScale).forEach(([shade, color]) => {
    vars[`--color-${prefix}-${shade}`] = color;
  });

  return vars;
};

/**
 * Generate all color CSS variables
 */
const generateAllColorVars = (
  preset: ColorPreset,
  mode: 'light' | 'dark'
): Record<string, string> => ({
  ...generateColorVars('primary', preset.primary, mode),
  ...generateColorVars('secondary', preset.secondary, mode),
  ...generateColorVars('tertiary', preset.tertiary, mode),
  ...generateColorVars('neutral', preset.neutral, mode),
  ...generateColorVars('success', preset.semantic.success, mode),
  ...generateColorVars('warning', preset.semantic.warning, mode),
  ...generateColorVars('error', preset.semantic.error, mode),
  ...generateColorVars('info', preset.semantic.info, mode),
});

/**
 * Generate spacing CSS variables with density
 */
const generateSpacingVars = (density: DensityLevel): Record<string, string> => {
  const vars: Record<string, string> = {};

  Object.entries(BASE_SPACING).forEach(([key, value]) => {
    vars[`--spacing-${key}`] = `${applyDensity(value, density)}px`;
  });

  return vars;
};

/**
 * Generate typography CSS variables
 */
const generateTypographyVars = (fontFamily: string): Record<string, string> => ({
  '--font-family': fontFamily,
});

// ============================================================================
// MAIN GENERATOR FUNCTION
// ============================================================================

/**
 * Generate complete theme variables
 */
export const generateThemeVariables = (config: ThemeConfig): ThemeVariables => ({
  colors: generateAllColorVars(config.colorPreset, config.mode),
  spacing: generateSpacingVars(config.density),
  typography: generateTypographyVars(config.fontFamily),
});

/**
 * Apply theme variables to document
 */
export const applyThemeVariables = (
  variables: ThemeVariables,
  element: HTMLElement = document.documentElement
): void => {
  // Apply all variables
  Object.entries(variables.colors).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });

  Object.entries(variables.spacing).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });

  Object.entries(variables.typography).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
};

/**
 * Generate and apply theme
 */
export const applyTheme = (config: ThemeConfig, element?: HTMLElement): void => {
  const variables = generateThemeVariables(config);
  applyThemeVariables(variables, element);

  // Update data attribute for CSS selectors
  const root = element ?? document.documentElement;
  root.setAttribute('data-theme', config.mode);
  root.setAttribute('data-color-preset', config.colorPreset.id);
  root.setAttribute('data-density', config.density);
};

/**
 * Remove theme variables
 */
export const removeThemeVariables = (element: HTMLElement = document.documentElement): void => {
  // Remove all CSS variables that start with --color-, --spacing-, --font-
  const styles = element.style;
  for (let i = styles.length - 1; i >= 0; i--) {
    const property = styles[i];
    if (
      property.startsWith('--color-') ||
      property.startsWith('--spacing-') ||
      property.startsWith('--font-')
    ) {
      element.style.removeProperty(property);
    }
  }

  // Remove data attributes
  element.removeAttribute('data-theme');
  element.removeAttribute('data-color-preset');
  element.removeAttribute('data-density');
};
